import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PaperPlaneTilt, Phone, ChatText, CheckCircle } from "@phosphor-icons/react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { PHONE, PHONE_RAW } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", dropoff: "", date: "", time: "", notes: "" };
const inputCls = "border-2 border-ink rounded-none h-12 focus-visible:ring-2 focus-visible:ring-brandblue";

export const SpecialBooking = ({ defaultService = "Special Arrangement (Gifts, Flowers, Cake)" }) => {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim()) {
      toast.error("Please add your name and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: f.name, phone: f.phone, email: f.email || undefined,
        service_type: defaultService, dropoff: f.dropoff, date: f.date, time: f.time,
        notes: f.notes,
      });
      setDone(true);
      setF(EMPTY);
      toast.success("Sent! We'll confirm your surprise shortly.");
    } catch {
      toast.error("Something went wrong. Please call or text us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" data-testid="special-booking-section" className="py-20 sm:py-28 bg-ink text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-start">
        <div className="lg:sticky lg:top-28">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sun mb-3">Book a surprise</p>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            Tell us the<br />plan. <span className="text-sun">We deliver.</span>
          </h2>
          <p className="mt-5 text-white/70 leading-relaxed max-w-md">
            Gifts, cakes, flowers or food — even at midnight. Fill this in and we'll confirm and give you a price. Prefer to talk? We're one call away, 24/7.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a data-testid="special-book-call" href={`tel:${PHONE_RAW}`} className="flex items-center gap-2 bg-sun text-ink font-bold px-5 py-3 border-2 border-sun hover:bg-transparent hover:text-sun transition-colors">
              <Phone weight="fill" size={18} /> {PHONE}
            </a>
            <a data-testid="special-book-text" href={`sms:${PHONE_RAW}`} className="flex items-center gap-2 font-bold px-5 py-3 border-2 border-white hover:bg-white hover:text-ink transition-colors">
              <ChatText weight="fill" size={18} /> Text us
            </a>
          </div>
        </div>

        {done ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="special-booking-success" className="bg-white text-ink border-2 border-sun p-10 flex flex-col items-start">
            <CheckCircle weight="fill" size={56} className="text-brandblue" />
            <h3 className="font-display font-extrabold text-3xl tracking-tight mt-4">Request received!</h3>
            <p className="mt-3 text-ink/70 leading-relaxed">We've got your surprise request and will confirm shortly. Need it now? Give us a call.</p>
            <button data-testid="special-booking-reset" onClick={() => setDone(false)}
              className="mt-6 bg-ink text-white font-bold px-6 py-3 border-2 border-ink hover:bg-sun hover:text-ink transition-colors">
              Send another
            </button>
          </motion.div>
        ) : (
          <form data-testid="special-booking-form" onSubmit={submit} className="bg-white text-ink border-2 border-sun p-6 sm:p-8">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Name *"><Input data-testid="sb-name" value={f.name} onChange={set("name")} placeholder="Your name" className={inputCls} /></Field>
              <Field label="Phone *"><Input data-testid="sb-phone" value={f.phone} onChange={set("phone")} placeholder="(902) 000-0000" className={inputCls} /></Field>
              <Field label="Email (for receipt)"><Input data-testid="sb-email" type="email" value={f.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} /></Field>
              <Field label="Deliver to"><Input data-testid="sb-dropoff" value={f.dropoff} onChange={set("dropoff")} placeholder="Delivery address" className={inputCls} /></Field>
              <Field label="Date"><Input data-testid="sb-date" type="date" value={f.date} onChange={set("date")} className={inputCls} /></Field>
              <Field label="Time"><Input data-testid="sb-time" type="time" value={f.time} onChange={set("time")} className={inputCls} /></Field>
            </div>
            <div className="mt-5">
              <Field label="What should we bring? *"><Textarea data-testid="sb-notes" value={f.notes} onChange={set("notes")} rows={3} placeholder="e.g. Birthday cake + a dozen red roses, card message…" className="border-2 border-ink rounded-none focus-visible:ring-2 focus-visible:ring-brandblue" /></Field>
            </div>
            <button data-testid="special-booking-submit" type="submit" disabled={loading}
              className="mt-6 w-full flex items-center justify-center gap-2.5 bg-sun text-ink font-bold text-lg py-4 border-2 border-ink shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all duration-150 disabled:opacity-60">
              <PaperPlaneTilt weight="fill" size={20} /> {loading ? "Sending…" : "Send my request"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-ink/60">{label}</Label>
    {children}
  </div>
);
